import { useEffect, useState } from 'react';
import { Card, Group, Stack, Text, Title, Button, Loader, Progress, Box, Modal } from '@mantine/core';

export interface ItineraryBuildingProps {
  onCancel?: () => void;
}

const stages = [
  'Initializing data sources',
  'Collecting destination intelligence',
  'Scoring route and timing options',
  'Optimizing sequence & constraints',
  'Refining day-by-day structure',
  'Enhancing local details & context',
  'Finalizing itinerary draft',
  'Reviewing for quality & coherence',
];

export function ItineraryBuilding({ onCancel }: ItineraryBuildingProps) {
  const [start] = useState(() => Date.now());
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);

  const intervalSec = 20;
  const elapsedSec = Math.floor((now - start) / 1000);
  const rawIndex = Math.floor(elapsedSec / intervalSec);
  const stageIndex = rawIndex % stages.length;
  const current = stages[stageIndex];

  const cyclicalProgress = ((elapsedSec % intervalSec) / intervalSec) * 100;

  return (
    <Modal
      opened
      onClose={() => {}}
      withCloseButton={false}
      centered
      size="sm"
      overlayProps={{ backgroundOpacity: 0.55, blur: 2 }}
    >
      <Card withBorder shadow="md" radius="md" p="lg">
        <Stack gap="md" align="center">
          <Group gap="sm" align="flex-start">
            <Loader size="sm" type="oval" />
            <Box>
              <Title order={4}>Building your itinerary</Title>
              <Text size="sm" c="dimmed">
                Please be patient while we create your personalized itinerary.
              </Text>
            </Box>
          </Group>
          <Text size="sm" ta="center" fw={500}>
            {current}...
          </Text>
          <Progress
            value={cyclicalProgress}
            size="xs"
            radius="xl"
            w="100%"
            animated={true}
            aria-label="Itinerary build progress"
          />
          {onCancel && (
            <Button size="compact-xs" variant="subtle" color="gray" onClick={onCancel} mt={4}>
              Cancel
            </Button>
          )}
        </Stack>
      </Card>
    </Modal>
  );
}

export default ItineraryBuilding;
